import {
  AlertCircle,
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  Copy,
  Inbox,
  LoaderCircle,
  Search,
  X,
} from "lucide-react";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from "react";
import { Link } from "@tanstack/react-router";
import { formatNumber, initials } from "../api";

export function Button({ className = "", variant = "secondary", size = "medium", loading, children, disabled, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "small" | "medium" | "large";
  loading?: boolean;
}) {
  return (
    <button className={`button button-${variant} button-${size} ${className}`} disabled={disabled || loading} {...props}>
      {loading && <LoaderCircle className="spin" size={15} />}
      {children}
    </button>
  );
}

export function IconButton({ label, className = "", children, ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button className={`icon-button ${className}`} aria-label={label} title={label} {...props}>
      {children}
    </button>
  );
}

export function Badge({ tone = "neutral", children, className = "" }: { tone?: "neutral" | "indigo" | "green" | "gold" | "red" | "blue" | "violet" | "teal"; children: ReactNode; className?: string }) {
  return <span className={`badge badge-${tone} ${className}`}>{children}</span>;
}

export function StatusBadge({ status }: { status?: string | null }) {
  const value = status || "unknown";
  const tone = ["active", "healthy", "ready", "resolved", "online", "success", "accepted"].includes(value.toLowerCase())
    ? "green"
    : ["urgent", "failed", "error", "disabled", "archived", "offline", "expired", "revoked"].includes(value.toLowerCase())
      ? "red"
      : ["investigating", "warning", "waiting", "degraded", "pending", "claiming"].includes(value.toLowerCase())
        ? "gold"
        : ["owner", "operator", "open"].includes(value.toLowerCase())
          ? "indigo"
          : "neutral";
  return <Badge tone={tone}><span className="status-dot" />{humanize(value)}</Badge>;
}

export function PriorityBadge({ priority }: { priority?: string | null }) {
  const value = priority || "normal";
  return <Badge tone={value === "urgent" ? "red" : value === "high" ? "gold" : value === "low" ? "neutral" : "blue"}>{humanize(value)}</Badge>;
}

export function humanize(value: string): string {
  return value.replace(/[_-]+/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

export function operationalLabel(value: string): string {
  return humanize(value).replace(/\bMaps\b/g, "Boards").replace(/\bMap\b/g, "Board");
}

export function Panel({ className = "", children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <section className={`panel ${className}`} {...props}>{children}</section>;
}

export function PanelHeader({ title, eyebrow, description, action }: { title: string; eyebrow?: string; description?: string; action?: ReactNode }) {
  return (
    <div className="panel-header">
      <div>
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        <h2>{title}</h2>
        {description && <p>{description}</p>}
      </div>
      {action && <div className="panel-actions">{action}</div>}
    </div>
  );
}

export function PageHeader({ title, description, eyebrow, actions, back }: { title: string; description?: string; eyebrow?: string; actions?: ReactNode; back?: string }) {
  return (
    <header className="page-header">
      <div className="page-title-block">
        {back && <Link to={back} className="back-link"><ArrowLeft size={15} /> Back</Link>}
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function SearchField({ className = "", ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className={`search-field ${className}`}>
      <Search size={16} />
      <input type="search" {...props} />
    </label>
  );
}

export function Avatar({ name, email, image, size = "medium" }: { name?: string | null; email?: string | null; image?: string | null; size?: "small" | "medium" | "large" }) {
  const [broken, setBroken] = useState(false);
  return (
    <span className={`avatar avatar-${size}`} aria-hidden="true">
      {image && !broken ? <img src={image} alt="" onError={() => setBroken(true)} /> : initials(name, email)}
    </span>
  );
}

export function PersonCell({ name, email, image }: { name?: string | null; email?: string | null; image?: string | null }) {
  return (
    <div className="person-cell">
      <Avatar name={name} email={email} image={image} size="small" />
      <span><strong>{name || email || "Unknown person"}</strong>{name && email && <small>{email}</small>}</span>
    </div>
  );
}

export function LoadingState({ label = "Gathering the details" }: { label?: string }) {
  return (
    <div className="state-block loading-state" role="status">
      <span className="thinking-dots"><i /><i /><i /></span>
      <strong>{label}</strong>
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : "Something unexpected happened.";
  return (
    <div className="state-block error-state" role="alert">
      <span className="state-icon"><AlertCircle size={22} /></span>
      <strong>That view could not be loaded</strong>
      <p>{message}</p>
      {onRetry && <Button onClick={onRetry}>Try again</Button>}
    </div>
  );
}

export function EmptyState({ title, description, icon, action }: { title: string; description: string; icon?: ReactNode; action?: ReactNode }) {
  return (
    <div className="state-block empty-state">
      <span className="state-icon">{icon ?? <Inbox size={22} />}</span>
      <strong>{title}</strong>
      <p>{description}</p>
      {action}
    </div>
  );
}

export function StatCard({ label, value, detail, icon, accent = "indigo", delta }: { label: string; value?: number | string; detail?: string; icon: ReactNode; accent?: "indigo" | "coral" | "sky" | "leaf" | "gold" | "rose" | "teal"; delta?: number }) {
  return (
    <Panel className={`stat-card stat-${accent}`}>
      <div className="stat-top"><span className="stat-icon">{icon}</span>{typeof delta === "number" && <Badge tone={delta >= 0 ? "green" : "red"}>{delta >= 0 ? "+" : ""}{delta}%</Badge>}</div>
      <strong className="stat-value">{typeof value === "number" ? formatNumber(value) : value ?? "0"}</strong>
      <span className="stat-label">{label}</span>
      {detail && <small>{detail}</small>}
    </Panel>
  );
}

export function Pagination({ page, pages, total, onPage }: { page: number; pages: number; total: number; onPage: (page: number) => void }) {
  if (pages <= 1 && total === 0) return null;
  return (
    <div className="pagination">
      <span>{formatNumber(total)} total</span>
      <div>
        <IconButton label="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)}><ChevronLeft size={16} /></IconButton>
        <span className="page-count">{page} of {Math.max(1, pages)}</span>
        <IconButton label="Next page" disabled={page >= pages} onClick={() => onPage(page + 1)}><ChevronRight size={16} /></IconButton>
      </div>
    </div>
  );
}

export function Modal({ open, onClose, title, description, children, footer, size = "medium" }: { open: boolean; onClose: () => void; title: string; description?: string; children: ReactNode; footer?: ReactNode; size?: "small" | "medium" | "large" }) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.classList.add("modal-open");
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("modal-open");
      previous?.focus();
    };
  }, [open]);
  if (!open) return null;
  return (
    <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <div className={`modal modal-${size}`} role="dialog" aria-modal="true" aria-labelledby={titleId} ref={panelRef} tabIndex={-1}>
        <div className="modal-header">
          <div><h2 id={titleId}>{title}</h2>{description && <p>{description}</p>}</div>
          <IconButton label="Close" onClick={onClose}><X size={18} /></IconButton>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
  title: string;
  description: string;
  confirmLabel: string;
  tone?: "danger" | "primary";
  requireReason?: boolean;
  reasonLabel?: string;
  loading?: boolean;
}

export function ConfirmDialog(props: ConfirmDialogProps) {
  if (!props.open) return null;
  return <ConfirmDialogContent {...props} />;
}

function ConfirmDialogContent({ onClose, onConfirm, title, description, confirmLabel, tone = "danger", requireReason = false, reasonLabel = "Reason", loading = false }: ConfirmDialogProps) {
  const [reason, setReason] = useState("");
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      description={description}
      size="small"
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant={tone} loading={loading} disabled={requireReason && reason.trim().length < 3} onClick={() => onConfirm(reason.trim())}>{confirmLabel}</Button></>}
    >
      {requireReason && <Field label={reasonLabel} hint="This is written to the audit trail."><textarea rows={4} value={reason} onChange={(event) => setReason(event.target.value)} autoFocus placeholder="Add a clear operational reason" /></Field>}
    </Modal>
  );
}

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {error ? <small className="field-error">{error}</small> : hint ? <small>{hint}</small> : null}
    </label>
  );
}

export function DetailList({ items }: { items: Array<{ label: string; value: ReactNode }> }) {
  return (
    <dl className="detail-list">
      {items.map((item) => <div key={item.label}><dt>{item.label}</dt><dd>{item.value}</dd></div>)}
    </dl>
  );
}

export function CopyValue({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button className="copy-value" type="button" onClick={async () => {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    }} title={`Copy ${label ?? "value"}`}>
      <code>{value}</code>{copied ? <Check size={13} /> : <Copy size={13} />}
    </button>
  );
}

export function JsonViewer({ data }: { data: unknown }) {
  return <pre className="json-viewer">{JSON.stringify(data ?? {}, null, 2)}</pre>;
}

export function SegmentedControl<T extends string>({ value, options, onChange, label }: { value: T; options: Array<{ value: T; label: string }>; onChange: (value: T) => void; label: string }) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option) => <button type="button" key={option.value} className={option.value === value ? "is-active" : ""} onClick={() => onChange(option.value)}>{option.label}</button>)}
    </div>
  );
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd>{children}</kbd>;
}
